import { useMemo } from 'react';

import { getGroupByLabel } from '@/modules/analytics/constants/metricsCatalog';
import { useReportMetricSeries } from '@/modules/analytics/hooks/useReportMetric';
import {
  type ReportMetricTableRow,
  useReportMetricTable,
} from '@/modules/analytics/hooks/useReportMetricTable';
import type { MetricExportEntry } from '@/modules/analytics/types';
import type { ReportMetric } from '@/shared/types/reports';

const NO_GROUP_COLUMNS: string[] = [];
const NO_BREAKDOWN: ReportMetricTableRow[] = [];

export const useMetricExportEntry = (metric: ReportMetric, index: number, name: string) => {
  const seriesQuery = useReportMetricSeries(metric);
  const hasGrouping = (metric.query.groupBy?.length ?? 0) > 0;
  const tableQuery = useReportMetricTable(hasGrouping ? metric : null);

  const { series, valueFormatter } = seriesQuery;
  const chartReady = !seriesQuery.isLoading && !seriesQuery.isError;
  const tableReady = !hasGrouping || (!tableQuery.isLoading && !tableQuery.isError);
  const isSettled = !seriesQuery.isLoading && (!hasGrouping || !tableQuery.isLoading);
  const hasData = hasGrouping ? tableQuery.rows.length > 0 : series.some((s) => s.data.length > 0);

  const groupColumns = hasGrouping ? tableQuery.groupColumns : NO_GROUP_COLUMNS;
  const groupColumnLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const column of groupColumns) {
      labels[column] = getGroupByLabel(column) ?? column;
    }
    return labels;
  }, [groupColumns]);
  const entry = useMemo<MetricExportEntry>(
    () => ({
      index,
      name,
      metricId: metric.metricId,
      range: metric.range,
      series,
      groupColumns,
      groupColumnLabels,
      breakdown: hasGrouping ? tableQuery.rows : NO_BREAKDOWN,
      valueFormatter,
      hasData,
    }),
    [
      index,
      name,
      metric.metricId,
      metric.range,
      series,
      groupColumns,
      groupColumnLabels,
      hasGrouping,
      tableQuery.rows,
      valueFormatter,
      hasData,
    ]
  );

  return { entry, seriesQuery, tableQuery, hasGrouping, chartReady, tableReady, isSettled };
};
